/**
 * firstRunPrompts: the one place that decides WHEN an optional ask may appear.
 *
 * WHY THIS EXISTS (owner report, 2026-10-23)
 *   A brand-new account was met with everything at once: the tour, the iOS
 *   notification sheet, "How did you find Nexus Dex?", the feature-idea nudge,
 *   and before long the review ask and the Pro sheet. Each prompt kept its own
 *   flag and its own delay and peeked at the others' flags to queue behind
 *   them, so the order lived in four files and the spacing was whatever the
 *   timers happened to be. This module owns both.
 *
 * THE RULES
 *   - The schedule knows nothing about what a prompt looks like. It answers one
 *     question: which optional prompt, if any, may show in this session.
 *   - At most ONE prompt per app session, and at least MIN_GAP_MS between any
 *     two, so a trainer who opens the app five times a day still meets at most
 *     one ask that day.
 *   - Each prompt has a floor: the session it may first appear in, how old the
 *     account must be, and whether the trainer must have scanned something
 *     first. The ones that need context (feedback, a review) wait for real use.
 *   - The order of FIRST_RUN_PROMPTS is the priority: when several are due in
 *     the same session, the earliest wins and the rest wait for another day.
 *   - Answered or dismissed is forever. Each prompt's existing "asked" key is
 *     honoured, so nobody who was asked before this shipped is asked again.
 *     For the store rating the SHOWING is the answer (owner, 2026-10-26: a
 *     trainer who has given their feedback is not asked again): once the OS
 *     sheet has been requested on a device, it is never requested there
 *     again. The OS never says whether stars were tapped, so "asked once" is
 *     the closest the record can get to "rated once".
 *   - The schedule can say WHY it said no (whyPromptNotDue), so a device
 *     report names the floor, the gap or the earlier ask that held a prompt back;
 *     and an admin can reset the review's record (resetReviewPrompt).
 *
 * WHAT IS NOT HERE
 *   The legal splash, the trainer step (name and level) and the tour are not
 *   optional and are not scheduled. The tour does CLAIM the session it opens
 *   in (SessionProvider), which is what keeps day one to the tour alone.
 *
 * A "session" is a foreground visit: a launch, or a return to the foreground
 * after SESSION_IDLE_MS away. The same rule the admin heartbeat uses for an
 * open, with a longer gap. Everything persists in one localStorage record, the
 * same store the prompts' own flags already live in, so it survives a restart
 * and an iOS eviction alike.
 */

export type FirstRunPromptId =
  | "tour"
  | "live-events"
  | "heard-about"
  | "coachmark"
  | "notifications"
  | "why-pro"
  | "feature-idea"
  | "review";

export interface FirstRunPromptRule {
  id: Exclude<FirstRunPromptId, "tour">;
  /** The first session (1-based) this prompt may appear in. */
  minSession: number;
  /** The account must be at least this old. Unknown ages count as old. */
  minAccountAgeMs: number;
  /** Only once scan rows have reached the server (or the server says so). */
  afterFirstScan: boolean;
  /**
   * The prompt's own "asked" key from before this module existed. Read as
   * done and written on done, so the prompt's older readers keep working and
   * a device that was asked before this shipped is never asked again.
   */
  legacyDoneKey: string | null;
  /**
   * The showing is the answer: once this prompt has been put to the trainer
   * (its shown stamp), it is done, answered or not. For the OS rating sheet,
   * which never reports what the trainer did with it. Cards leave it unset:
   * a card shown and not yet answered may still be answered.
   */
  askedOnce?: boolean;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Away this long and the next foreground is a new session. */
export const SESSION_IDLE_MS = 30 * 60 * 1000;
/**
 * The least time between two prompts, whatever the session count says.
 * Twenty hours rather than a day, so "once a day" survives a trainer who
 * opens the app at the same time each morning.
 */
export const MIN_GAP_MS = 20 * 60 * 60 * 1000;

/**
 * THE ORDER AND THE GAPS. Earlier wins when several are due at once.
 *
 *   live-events    the one-time set-up of the automatic event countdowns
 *                  (2026-10-30): which event types, how long before, card or
 *                  notification. First in line: it is a launch announcement
 *                  for every trainer, new and old, and the choice it collects
 *                  is what makes the Calendar's reminders automatic. From the
 *                  second session, so day one still belongs to the tour. An
 *                  account that already holds a choice (set on another phone)
 *                  marks it done without showing it.
 *   heard-about    from the second session: attribution has a shelf life, and
 *                  day one belongs to the tour.
 *   coachmark      "Long-press for quick actions" over the medallion, from the
 *                  second session. It used to fire on the first mount, under
 *                  the tour overlay, and burn itself unseen in four seconds.
 *   notifications  the OS permission sheet, from the third session. Nothing on
 *                  day one needs push; the reminders and the broadcast pill
 *                  still ask in context whenever they are used.
 *   why-pro        the one-time Basic vs Pro sheet: a return visit (a day old),
 *                  never the first entry (owner, 2026-09-11).
 *   feature-idea   feedback needs something to give feedback on: after the
 *                  first scan, three sessions and a day.
 *   review         the store rating sheet (lib/storeReview): after the first
 *                  scan, three sessions and three days (the 2026-09-16 rule),
 *                  and after feedback has had its chance, so an unhappy
 *                  trainer finds the feedback door before the stars. Unlike
 *                  the cards it is never shown on a timer: it is only REQUESTED
 *                  at a moment of success (a dex milestone, a scan that added
 *                  entries), and only when the schedule says it is this
 *                  session's one ask. Once requested on a device it is never
 *                  requested there again (askedOnce; owner, 2026-10-26).
 */
export const FIRST_RUN_PROMPTS: readonly FirstRunPromptRule[] = [
  { id: "live-events", minSession: 2, minAccountAgeMs: 0, afterFirstScan: false, legacyDoneKey: null },
  { id: "heard-about", minSession: 2, minAccountAgeMs: 0, afterFirstScan: false, legacyDoneKey: "nx.acq.asked" },
  { id: "coachmark", minSession: 2, minAccountAgeMs: 0, afterFirstScan: false, legacyDoneKey: "nexusdex.coachmark.medallionLongPress.v1" },
  { id: "notifications", minSession: 3, minAccountAgeMs: 0, afterFirstScan: false, legacyDoneKey: null },
  { id: "why-pro", minSession: 2, minAccountAgeMs: DAY_MS, afterFirstScan: false, legacyDoneKey: "nx.whyPro.seen.v1" },
  { id: "feature-idea", minSession: 3, minAccountAgeMs: DAY_MS, afterFirstScan: true, legacyDoneKey: "nx.featureIdea.asked.v1" },
  {
    id: "review",
    minSession: 3,
    minAccountAgeMs: 3 * DAY_MS,
    afterFirstScan: true,
    legacyDoneKey: "nx.review.asked.v1",
    askedOnce: true,
  },
];

export interface FirstRunState {
  /** Foreground visits on this device. 0 until the first begins. */
  sessions: number;
  /** When the current session began (ms epoch). */
  sessionStartedAt: number;
  /** The last foreground or background tick (ms epoch). */
  lastActiveAt: number;
  /** When scan rows first reached the server from this device, or the server said they had. */
  firstScanAt: number | null;
  /** When the most recent prompt (the tour included) was shown. */
  lastPromptAt: number | null;
  /** The session the most recent prompt was shown in. */
  lastPromptSession: number | null;
  /** Shown, by prompt: the ms epoch of the latest showing. */
  shown: Partial<Record<FirstRunPromptId, number>>;
  /** Answered or dismissed, by prompt: the ms epoch. Forever. */
  done: Partial<Record<FirstRunPromptId, number>>;
}

export const FIRST_RUN_STATE_KEY = "nx.firstRunPrompts.v1";

const EMPTY: FirstRunState = {
  sessions: 0,
  sessionStartedAt: 0,
  lastActiveAt: 0,
  firstScanAt: null,
  lastPromptAt: null,
  lastPromptSession: null,
  shown: {},
  done: {},
};

function asNumber(v: unknown): number {
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}
function asNumberOrNull(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
function asRecord(v: unknown): Partial<Record<FirstRunPromptId, number>> {
  if (!v || typeof v !== "object") return {};
  const out: Partial<Record<FirstRunPromptId, number>> = {};
  for (const [k, n] of Object.entries(v as Record<string, unknown>)) {
    if (typeof n === "number" && Number.isFinite(n)) out[k as FirstRunPromptId] = n;
  }
  return out;
}

export function readFirstRunState(): FirstRunState {
  if (typeof localStorage === "undefined") return { ...EMPTY, shown: {}, done: {} };
  try {
    const raw = localStorage.getItem(FIRST_RUN_STATE_KEY);
    if (!raw) return { ...EMPTY, shown: {}, done: {} };
    const p = JSON.parse(raw) as Partial<Record<keyof FirstRunState, unknown>>;
    return {
      sessions: asNumber(p.sessions),
      sessionStartedAt: asNumber(p.sessionStartedAt),
      lastActiveAt: asNumber(p.lastActiveAt),
      firstScanAt: asNumberOrNull(p.firstScanAt),
      lastPromptAt: asNumberOrNull(p.lastPromptAt),
      lastPromptSession: asNumberOrNull(p.lastPromptSession),
      shown: asRecord(p.shown),
      done: asRecord(p.done),
    };
  } catch {
    return { ...EMPTY, shown: {}, done: {} };
  }
}

// ── A tiny store, so React can follow writes (useFirstRunPrompt) ────────────
let version = 0;
const listeners = new Set<() => void>();

function write(next: FirstRunState): void {
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.setItem(FIRST_RUN_STATE_KEY, JSON.stringify(next));
    } catch {
      /* quota or private mode: at worst a prompt comes a session early */
    }
  }
  version += 1;
  for (const l of listeners) l();
}

/** subscribe for useSyncExternalStore. */
export function subscribeFirstRunPrompts(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** getSnapshot for useSyncExternalStore: bumps on every write. */
export function getFirstRunVersion(): number {
  return version;
}

function update(mutate: (s: FirstRunState) => void): FirstRunState {
  const s = readFirstRunState();
  mutate(s);
  write(s);
  return s;
}

// ── Sessions ────────────────────────────────────────────────────────────────

/** The session the device is in. 1 before any has been begun. */
export function currentFirstRunSession(state: FirstRunState = readFirstRunState()): number {
  return Math.max(1, state.sessions);
}

/**
 * The app came to the foreground. A new session begins when none has, or
 * when the last tick is SESSION_IDLE_MS or more ago; otherwise this is the
 * same visit (a reload, a quick switch to Pokémon GO and back).
 */
export function beginFirstRunSession(now: number = Date.now()): FirstRunState {
  return update((s) => {
    if (s.sessions === 0 || now - s.lastActiveAt >= SESSION_IDLE_MS) {
      s.sessions += 1;
      s.sessionStartedAt = now;
    }
    s.lastActiveAt = now;
  });
}

/** The app went to the background: remember when, so the idle gap is honest. */
export function touchFirstRunSession(now: number = Date.now()): void {
  update((s) => {
    s.lastActiveAt = now;
  });
}

/**
 * Begin a session now and on every return to the foreground. Returns the
 * teardown. Mirrors startAppHeartbeat: on a phone the page is not reloaded
 * when the app comes back, so a mount-only count would stop at one.
 */
export function startFirstRunSessions(): () => void {
  beginFirstRunSession();
  if (typeof document === "undefined") return () => {};
  const onVisibility = () => {
    if (document.visibilityState === "visible") beginFirstRunSession();
    else touchFirstRunSession();
  };
  document.addEventListener("visibilitychange", onVisibility);
  return () => document.removeEventListener("visibilitychange", onVisibility);
}

// ── Milestones and answers ──────────────────────────────────────────────────

/** Scan rows reached the server (dexStateStore), or the server says they have. */
export function noteFirstRunScan(now: number = Date.now()): void {
  if (readFirstRunState().firstScanAt != null) return;
  update((s) => {
    s.firstScanAt = now;
  });
}

/**
 * A prompt is on screen: this session is spent. Called the moment it shows,
 * not when it is answered, so a prompt left unanswered still counts as the
 * one ask of the day.
 */
export function claimFirstRunPrompt(id: FirstRunPromptId, now: number = Date.now()): void {
  update((s) => {
    s.shown[id] = now;
    s.lastPromptAt = now;
    s.lastPromptSession = currentFirstRunSession(s);
  });
}

function ruleFor(id: FirstRunPromptId): FirstRunPromptRule | undefined {
  return FIRST_RUN_PROMPTS.find((r) => r.id === id);
}

/** Answered or dismissed: never again, on this device. */
export function markFirstRunPromptDone(id: FirstRunPromptId, now: number = Date.now()): void {
  const legacy = ruleFor(id)?.legacyDoneKey;
  if (legacy && typeof localStorage !== "undefined") {
    try {
      localStorage.setItem(legacy, "1");
    } catch {
      /* the record below still carries it */
    }
  }
  update((s) => {
    s.done[id] = now;
  });
}

export function isFirstRunPromptDone(id: FirstRunPromptId, state: FirstRunState = readFirstRunState()): boolean {
  if (state.done[id] != null) return true;
  const rule = ruleFor(id);
  if (rule?.askedOnce && state.shown[id] != null) return true;
  return legacyFlagSet(rule?.legacyDoneKey);
}

function legacyFlagSet(key: string | null | undefined): boolean {
  if (!key || typeof localStorage === "undefined") return false;
  try {
    return localStorage.getItem(key) != null;
  } catch {
    // Unreadable storage: better never to ask than to ask on every launch.
    return true;
  }
}

/**
 * When this prompt was last put to the trainer, answered or not: the later of
 * its showing and its answer. Null when the record holds no date (never, or a
 * device whose legacy flag alone says "asked"; isFirstRunPromptDone tells
 * those apart from never).
 */
export function lastFirstRunPromptAskedAt(
  id: FirstRunPromptId,
  state: FirstRunState = readFirstRunState(),
): number | null {
  const stamps = [state.done[id], state.shown[id]].filter((n): n is number => typeof n === "number");
  return stamps.length > 0 ? Math.max(...stamps) : null;
}

// ── The decision ────────────────────────────────────────────────────────────

export interface FirstRunPickContext {
  now?: number;
  /** When the account was created (ms epoch). Null or unknown counts as old. */
  accountCreatedAt: number | null;
  /**
   * Which prompts this build and this account can show at all: the review
   * only where a store review can be left, the OS sheet only on a phone, the
   * Pro sheet only to a settled Basic trainer, attribution only while the
   * account has no answer. Missing means available.
   */
  available?: Partial<Record<FirstRunPromptId, boolean>>;
  state?: FirstRunState;
}

/** ms epoch from the shapes an account's createdAt arrives in. */
export function accountCreatedMs(createdAt: string | number | null | undefined): number | null {
  if (createdAt == null || createdAt === "") return null;
  const t =
    typeof createdAt === "number" ? (createdAt < 1e12 ? createdAt * 1000 : createdAt) : Date.parse(createdAt);
  return Number.isFinite(t) ? t : null;
}

/**
 * Why a prompt is not due right now: one shape per rule, carrying the
 * numbers a device report needs. Null means it IS due (pickDuePrompt would
 * name it). lib/storeReview logs the review's through describeFirstRunBlock,
 * so a tester can read off which gate stopped the rating sheet (2026-10-26:
 * the owner scanned on the Galaxy, saw no sheet, and could not tell why).
 */
export type FirstRunBlock =
  /** ctx.available says this build or account cannot show it at all. */
  | { kind: "unavailable" }
  /** This session already had its prompt (the tour counts). */
  | { kind: "session-spent"; session: number }
  /** The last prompt, whichever session, was under MIN_GAP_MS ago. */
  | { kind: "gap"; sinceMs: number }
  /** Answered or dismissed, or (askedOnce) shown: forever, on this device. */
  | { kind: "done" }
  | { kind: "session-floor"; session: number; minSession: number }
  | { kind: "age-floor"; ageMs: number; minAccountAgeMs: number }
  | { kind: "scan-floor" }
  /** An earlier rule is due in this session instead; this one waits for another day. */
  | { kind: "outranked"; by: FirstRunPromptId };

interface ResolvedPick {
  now: number;
  s: FirstRunState;
  session: number;
  ageMs: number;
}

function resolvePick(ctx: FirstRunPickContext): ResolvedPick {
  const now = ctx.now ?? Date.now();
  const s = ctx.state ?? readFirstRunState();
  return {
    now,
    s,
    session: currentFirstRunSession(s),
    ageMs: ctx.accountCreatedAt == null ? Number.POSITIVE_INFINITY : now - ctx.accountCreatedAt,
  };
}

/** What closes the whole session to optional prompts, whichever rule asks. */
function sessionBlock({ now, s, session }: ResolvedPick): FirstRunBlock | null {
  if (s.lastPromptSession === session) return { kind: "session-spent", session };
  if (s.lastPromptAt != null && now - s.lastPromptAt < MIN_GAP_MS) {
    return { kind: "gap", sinceMs: now - s.lastPromptAt };
  }
  return null;
}

/** What holds one rule back, its floors in order, or null when it is due. */
function ruleBlock(
  rule: FirstRunPromptRule,
  { now, s, session, ageMs }: ResolvedPick,
  available: FirstRunPickContext["available"],
): FirstRunBlock | null {
  if (available?.[rule.id] === false) return { kind: "unavailable" };
  if (isFirstRunPromptDone(rule.id, s)) return { kind: "done" };
  if (session < rule.minSession) return { kind: "session-floor", session, minSession: rule.minSession };
  if (ageMs < rule.minAccountAgeMs) return { kind: "age-floor", ageMs, minAccountAgeMs: rule.minAccountAgeMs };
  if (rule.afterFirstScan && s.firstScanAt == null) return { kind: "scan-floor" };
  return null;
}

/**
 * The single optional prompt that may show in this session, or null.
 *
 * Null when this session has already had its prompt (the tour counts), when
 * the last prompt was under MIN_GAP_MS ago, or when nothing has reached its
 * floor yet. Otherwise the first rule, in priority order, that is available,
 * not done (or, for a repeating prompt, not asked within its repeat gap), and
 * past its session, age and scan floors.
 */
export function pickDuePrompt(ctx: FirstRunPickContext): FirstRunPromptId | null {
  const r = resolvePick(ctx);
  if (sessionBlock(r)) return null;
  for (const rule of FIRST_RUN_PROMPTS) {
    if (!ruleBlock(rule, r, ctx.available)) return rule.id;
  }
  return null;
}

/**
 * Why `id` is not this session's prompt, or null when pickDuePrompt would
 * name it. The session rules first, then the rules in priority order: an
 * earlier one that is due is reported as "outranked"; otherwise `id`'s own
 * first unmet floor, in the order pickDuePrompt checks them.
 */
export function whyPromptNotDue(
  id: Exclude<FirstRunPromptId, "tour">,
  ctx: FirstRunPickContext,
): FirstRunBlock | null {
  const r = resolvePick(ctx);
  const session = sessionBlock(r);
  if (session) return session;
  for (const rule of FIRST_RUN_PROMPTS) {
    const block = ruleBlock(rule, r, ctx.available);
    if (rule.id === id) return block;
    if (!block) return { kind: "outranked", by: rule.id };
  }
  return null;
}

const HOUR_MS = 60 * 60 * 1000;

/** "2.1 h" under a day, "3 d" from there: enough to place a gate in time. */
export function formatFirstRunSpan(ms: number): string {
  if (ms < DAY_MS) {
    const h = ms / HOUR_MS;
    return Number.isInteger(h) ? `${h} h` : `${h.toFixed(1)} h`;
  }
  return `${Math.floor(ms / DAY_MS)} d`;
}

/** One short phrase per block, for a log line. No trainer data: counts and spans only. */
export function describeFirstRunBlock(block: FirstRunBlock): string {
  switch (block.kind) {
    case "unavailable":
      return "not available on this build or account";
    case "session-spent":
      return `session ${block.session} already had a prompt`;
    case "gap":
      return `under the ${formatFirstRunSpan(MIN_GAP_MS)} gap: last prompt ${formatFirstRunSpan(block.sinceMs)} ago`;
    case "done":
      return "already asked on this device";
    case "session-floor":
      return `session ${block.session}, needs ${block.minSession}`;
    case "age-floor":
      return `account ${formatFirstRunSpan(block.ageMs)} old, needs ${formatFirstRunSpan(block.minAccountAgeMs)}`;
    case "scan-floor":
      return "no scan from this device yet";
    case "outranked":
      return `behind ${block.by}`;
  }
}

// ── The admin reset ─────────────────────────────────────────────────────────

/**
 * Forget that the store rating was ever asked for on this device: the
 * review's shown and done stamps, and the old card's flag (its legacy key).
 * Admin only, from Settings (2026-10-26: the owner scanned on the Galaxy, saw
 * no sheet, and had no way to try again after the device's one ask).
 *
 * When the review was the last prompt shown, the session and the twenty-hour
 * gap it spent are released too, so the next moment of success may ask at
 * once. A gap another prompt spent stays: this resets the review, not the
 * schedule. The pending dwell moment is lib/ratingDwell's to drop
 * (lib/storeReview.resetRatingAsk does both).
 */
export function resetReviewPrompt(): void {
  const legacy = ruleFor("review")?.legacyDoneKey;
  if (legacy && typeof localStorage !== "undefined") {
    try {
      localStorage.removeItem(legacy);
    } catch {
      /* unreadable storage reads as asked; nothing more to do here */
    }
  }
  update((s) => {
    const reviewWasLast = s.lastPromptAt != null && [s.shown.review, s.done.review].includes(s.lastPromptAt);
    delete s.shown.review;
    delete s.done.review;
    if (reviewWasLast) {
      s.lastPromptAt = null;
      s.lastPromptSession = null;
    }
  });
}

/** Tests only: overwrite the record (and clear it with no argument). */
export function __setFirstRunStateForTest(partial: Partial<FirstRunState> = {}): void {
  write({ ...EMPTY, shown: {}, done: {}, ...partial });
}
